import '../Integration/Integrations.scss'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { ScrollNavigation, ScrollNavigationSection } from '../../components/NavigationWithSideBar/ScrollNavigation'
import React, { useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { OAuth2AppCard } from '../../components/OAuth2App/OAuth2AppCard/OAuth2AppCard'
import { ShortOAuth2App, useListOAuth2AppsQuery } from '../../store/auth/auth'
import { useOAuthApps } from '../Integration/useOAuthApps'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { ManagedAtEnterpriseLevelInfo } from '../../components/ManagedAtEnterpriseLevel/ManagedAtEnterpriseLevelInfo'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES } from '../Plan/DeltaTrial/DeltaTrialFeatures'

interface OAuth2AppSectionConfig {
  id: string
  label: string
  title?: string
  oAuth2Apps: ShortOAuth2App[]
}

const createOAuth2AppSection = ({ id, label, title, oAuth2Apps }: OAuth2AppSectionConfig): ScrollNavigationSection => {
  return {
    id,
    label,
    title,
    content: (
      <Container fluid className='px-0 pb-5'>
        <Row className='g-3'>
          {oAuth2Apps.map((oAuth2App) => (
            <Col key={oAuth2App.clientId} xs={12} sm={6} xxl={3} className='d-flex justify-content-center integration-settings__common-cards'>
              <OAuth2AppCard oAuth2App={oAuth2App} />
            </Col>
          ))}
        </Row>
      </Container>
    ),
  }
}

export const OAuth2AppDirectory: React.FC = () => {
  useDocumentTitle('Integrations')

  const { hasQuota, isFetching: quotaIsFetching, quotaSuccess } = useZocksQuota()
  const oauth2Enabled = hasQuota('integration.oauth2Enabled.value')

  const accountId = useAppSelector(selectAccountId)
  const { data: oAuth2AppData, isLoading: oAuth2AppIsLoading } = useListOAuth2AppsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true, skip: !oauth2Enabled },
  )
  const { isOAuthAppAllowed, accountIsLoading } = useOAuthApps()

  const oAuth2Apps: ShortOAuth2App[] = useMemo(() => {
    return (oAuth2AppData?.data ?? []).filter((app) => isOAuthAppAllowed(app.id))
  }, [oAuth2AppData, isOAuthAppAllowed])

  const sections: ScrollNavigationSection[] = useMemo(() => {
    return [
      createOAuth2AppSection({
        id: 'All',
        label: '',
        oAuth2Apps,
      }),
    ]
  }, [oAuth2Apps])

  if (quotaIsFetching || oAuth2AppIsLoading || accountIsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (quotaSuccess && !oauth2Enabled) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['appDirectory']} />
  }

  if (!oAuth2Apps.length) {
    return <ManagedAtEnterpriseLevelInfo resourceName={'Apps'} />
  }

  return (
    <ScrollNavigation sections={sections} defaultActiveSectionId='All' sectionClassName='integration-settings__integrations-container' showNavigation={false} />
  )
}
